import { CFDEventType, PaymentType } from '@lightspeed/cfd-event-types';
import get from 'lodash/get';
import last from 'lodash/last';

import { toBoolean } from 'pages/utils/sanitize';

function getTaxClasses(sale: Sale) {
  let taxClasses = get(sale, 'TaxClassTotals.Tax') as Tax[];
  if (!taxClasses) return {};

  if (!Array.isArray(taxClasses)) {
    taxClasses = [taxClasses];
  }

  return {
    TaxClassTotals: {
      Tax: taxClasses.map(
        ({ id, rate, amount, taxname, subtotal, rate2, amount2, taxname2, subtotal2 }) => ({
          id,
          rate: parseFloat(rate || '0'),
          amount: parseFloat(amount || '0'),
          taxname,
          subtotal: parseFloat(subtotal || '0'),
          rate2: parseFloat(rate2 || '0'),
          amount2: parseFloat(amount2 || '0'),
          taxname2,
          subtotal2: parseFloat(subtotal2 || '0'),
        })
      ),
    },
  };
}

function getSaleLineItem(saleLine: SaleLine) {
  if (!get(saleLine, 'Item')) return {};

  const itemNote = get(saleLine, 'Item.Note.note') as string | undefined;

  return {
    Item: {
      description: get(saleLine, 'Item.description', ''),
      ...(itemNote ? { Note: { note: itemNote } } : {}),
    },
  };
}

function getDiscount(saleLine: SaleLine) {
  if (!get(saleLine, 'Discount')) return {};

  return {
    Discount: {
      name: get(saleLine, 'Discount.name', ''),
      discountAmount: (parseFloat(get(saleLine, 'Discount.discountAmount', '0')) || -0) * -1,
      discountPercent: parseFloat(get(saleLine, 'Discount.discountPercent', '0')),
    },
  };
}

function getNote(saleLine: SaleLine) {
  if (!get(saleLine, 'Note')) return {};

  return {
    Note: {
      note: get(saleLine, 'Note.note', ''),
    },
  };
}

function getPaymentBySaleLineId(saleLine: SaleLine, sale: Sale) {
  const id = get(saleLine, 'saleLineID', '');
  let salePayment = get(sale, 'SalePayments.SalePayment');
  if (!Array.isArray(salePayment)) salePayment = [salePayment];

  return salePayment.find(
    (saleP: SalePayment) => get(saleP, 'SaleAccounts.SaleAccount.saleLineID') === id
  );
}

function getSaleLine(saleLine: SaleLine, sale: Sale) {
  const linkedPayment = getPaymentBySaleLineId(saleLine, sale);
  return {
    workOrder: get(saleLine, 'SaleLineWorkOrder'),
    tax: toBoolean(get(saleLine, 'tax')),
    saleLineID: parseFloat(get(saleLine, 'saleLineID', '0')),
    unitPrice: linkedPayment
      ? Math.abs(parseFloat(linkedPayment.amount))
      : parseFloat(get(saleLine, 'unitPrice', '0')),
    normalUnitPrice: parseFloat(get(saleLine, 'normalUnitPrice', '0')),
    displayableSubtotal: linkedPayment
      ? Math.abs(parseFloat(linkedPayment.amount))
      : parseFloat(get(saleLine, 'displayableSubtotal', '0')),
    calcLineDiscount: parseFloat(get(saleLine, 'calcLineDiscount', '0')) * -1,
    unitQuantity: parseFloat(get(saleLine, 'unitQuantity', '0')),
    ...getSaleLineItem(saleLine),
    ...getNote(saleLine),
    ...getDiscount(saleLine),
  };
}

function getSaleLines(sale: Sale) {
  if (!get(sale, 'SaleLines')) return {};

  const rawSaleLines = get(sale, 'SaleLines.SaleLine', []) as SaleLine[];
  const transformed = rawSaleLines.map((saleLine) => getSaleLine(saleLine, sale));

  const grouped = transformed.reduce((result, saleLine) => {
    const workOrder = get(saleLine, 'workOrder');
    if (!workOrder) {
      result.push(saleLine);
      return result;
    }

    if (workOrder.workOrderType === 'parent') {
      result.push({ ...saleLine, items: [] as typeof transformed });
      return result;
    }

    const parentIndex = result.findIndex(
      (line) => line.workOrder && line.workOrder.workOrderId === workOrder.workOrderId
    );
    if (parentIndex > -1) {
      result[parentIndex].items!.push(saleLine);
    }

    return result;
  }, [] as Array<(typeof transformed)[number] & { items?: typeof transformed }>);

  return { SaleLines: { SaleLine: grouped } };
}

function getCustomer(sale: Sale) {
  if (!get(sale, 'Customer')) return {};

  return {
    Customer: {
      customerID: get(sale, 'Customer.customerID', ''),
      firstName: get(sale, 'Customer.firstName', ''),
      lastName: get(sale, 'Customer.lastName', ''),
      company: get(sale, 'Customer.company', ''),
      Contact: {
        Phones: get(sale, 'Customer.Contact.Phones', ''),
        Emails: get(sale, 'Customer.Contact.Emails', ''),
      },
    },
  };
}

function getLastPayment(sale: Sale) {
  let salePayment = get(sale, 'SalePayments.SalePayment');

  if (!salePayment) return null;
  if (!Array.isArray(salePayment)) salePayment = [salePayment];

  return last(salePayment);
}

function getPaymentType(sale: Sale) {
  const payment = getLastPayment(sale);
  return payment ? get(payment, 'PaymentType.type', '') : '';
}

function getLastPaymentAmount(sale: Sale) {
  const payment = getLastPayment(sale);
  return payment ? parseFloat(get(payment, 'amount', '0')) : 0;
}

export const transformPayload = (sale: Sale) => ({
  saleID: get(sale, 'saleID', ''),
  ticketNumber: parseFloat(get(sale, 'ticketNumber', '0')),
  calcDiscount: (parseFloat(get(sale, 'calcDiscount', '0')) || -0) * -1,
  calcTax1: parseFloat(get(sale, 'calcTax1', '0')),
  calcTax2: parseFloat(get(sale, 'calcTax2', '0')),
  calcTotal: parseFloat(get(sale, 'totalDue', '0')),
  calcSubtotal: parseFloat(get(sale, 'displayableSubtotal', '0')),
  taxTotal: parseFloat(get(sale, 'taxTotal', '0')),
  completed: get(sale, 'completed', ''),
  archived: get(sale, 'archived', ''),
  voided: get(sale, 'voided', ''),
  createTime: get(sale, 'createTime', ''),
  updateTime: get(sale, 'updateTime', ''),
  quoteID: get(sale, 'quoteID', ''),
  ...getCustomer(sale),
  ...getSaleLines(sale),
  ...getTaxClasses(sale),
  paymentType: getPaymentType(sale),
  change: parseFloat(parseFloat(get(sale, 'change', '0')).toFixed(2)),
  calcTips: sale.tipEnabled ? parseFloat(parseFloat(get(sale, 'calcTips', '0')).toFixed(2)) : null,
  calcSurcharges: sale.surchargeEnabled
    ? parseFloat(parseFloat(get(sale, 'calcSurcharges', '0')).toFixed(2))
    : null,
});

/**
 * Normalize R-Series payment type to LSPay CFD PaymentType enum
 */
export function normalizeCFDPaymentType(rSeriesType: string): PaymentType {
  const type = rSeriesType.toLowerCase().trim();

  switch (type) {
    case 'cash':
      return PaymentType.CASH;
    case 'credit card':
    case 'debit':
      return PaymentType.CARD;
    case 'gift card':
      return PaymentType.GIFT_CARD;
    default:
      return PaymentType.UNKNOWN;
  }
}

/**
 * Transform sale payload to LSPay CFD SALE_COMPLETED format
 * @param sale - The sale object
 * @returns LSPay CFD formatted payload
 */
export const transformToLSPayCFDSaleCompleted = (sale: Sale) => {
  const paymentType = getPaymentType(sale);
  const lastPaymentAmount = getLastPaymentAmount(sale);
  const currency = window.merchantos.locale.currency_code;
  const discount = getLSPayDiscount(sale);
  const rawChange = get(sale, 'change', null);

  return {
    timestamp: new Date().toISOString(),
    version: 1,
    type: CFDEventType.SALE_COMPLETED,
    data: {
      paymentType: normalizeCFDPaymentType(paymentType),
      currency,
      lastPaymentAmount: parseFloat(lastPaymentAmount.toFixed(2)),
      ...(rawChange !== null ? { change: parseFloat(parseFloat(rawChange).toFixed(2)) } : {}),
      ...(discount ? { discount } : {}),
    },
  };
};

/**
 * Get LSPay CFD LineItemPromotion from R-Series sale line Discount
 */
function getLSPayLineItemPromotion(saleLine: SaleLine) {
  const discount = get(saleLine, 'Discount', null);
  if (discount === null) return {};

  const amount = Math.abs(parseFloat(get(discount, 'discountAmount', '0')));
  const percentage = parseFloat(get(discount, 'discountPercent', '0'));

  if (amount === 0 && percentage === 0) return {};

  return {
    promotion: {
      name: get(discount, 'name', ''),
      amount,
      ...(percentage !== 0 ? { percentage } : {}),
    },
  };
}

/**
 * Transform sale line items to LSPay CFD LineItem format
 */
function getLSPayLineItems(saleLines: SaleLine[]) {
  if (!saleLines || !Array.isArray(saleLines)) return [];

  return saleLines.map((saleLine) => ({
    id: String(get(saleLine, 'saleLineID', '')),
    name: get(saleLine, 'Item.description', ''),
    quantity: parseFloat(get(saleLine, 'unitQuantity', '0')),
    unitPrice: parseFloat(get(saleLine, 'unitPrice', '0')),
    basePrice: parseFloat(get(saleLine, 'normalUnitPrice', '0')),
    finalPrice: parseFloat(get(saleLine, 'displayableSubtotal', '0')),
    ...getLSPayLineItemPromotion(saleLine),
  }));
}

/**
 * Transform tax classes to LSPay CFD TaxItem format.
 * Each R-Series Tax entry can have two tax slots (taxname/amount and taxname2/amount2),
 * which are split into separate TaxItem entries.
 */
function getLSPayTaxes(sale: Sale) {
  let taxClasses = get(sale, 'TaxClassTotals.Tax') as Tax[];
  if (!taxClasses) return [];

  if (!Array.isArray(taxClasses)) {
    taxClasses = [taxClasses];
  }

  const taxes: { name: string; amount: number }[] = [];

  taxClasses.forEach((tax) => {
    const amount1 = parseFloat(String(tax.amount || '0'));
    if (tax.taxname && amount1 !== 0) {
      taxes.push({ name: tax.taxname, amount: amount1 });
    }

    const amount2 = parseFloat(String(tax.amount2 || '0'));
    if (tax.taxname2 && amount2 !== 0) {
      taxes.push({ name: tax.taxname2, amount: amount2 });
    }
  });

  return taxes;
}

/**
 * Get LSPay CFD Discount from sale-level calcDiscount
 */
function getLSPayDiscount(sale: Sale) {
  const rawDiscount = get(sale, 'calcDiscount', null);
  if (rawDiscount === null) return null;

  const discountAmount = Math.abs(parseFloat(rawDiscount));
  return { amount: discountAmount };
}

/**
 * Transform sale payload to LSPay CFD SALE_UPDATED format
 * @param sale - The sale object
 * @returns LSPay CFD SaleUpdatedEvent formatted payload
 */
export const transformToLSPayCFDSaleUpdated = (sale: Sale) => {
  const currency = window.merchantos.locale.currency_code;
  const saleLines = get(sale, 'SaleLines.SaleLine', []) as SaleLine[];
  const discount = getLSPayDiscount(sale);

  return {
    timestamp: new Date().toISOString(),
    version: 1,
    type: CFDEventType.SALE_UPDATED,
    data: {
      lineItems: getLSPayLineItems(Array.isArray(saleLines) ? saleLines : [saleLines]),
      currency,
      subTotal: parseFloat(get(sale, 'displayableSubtotal', '0')),
      tax: parseFloat(get(sale, 'taxTotal', '0')),
      taxes: getLSPayTaxes(sale),
      total: parseFloat(get(sale, 'totalDue', '0')),
      ...(discount ? { discount } : {}),
    },
  };
};
